import { Link } from "@inertiajs/react";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef } from "react";
import BibleLink from "./BibleLink";
import { useCurrentSearch } from "./routing";
import "./BibleBottomBar.css";

export default function BibleBottomBar({
  currentTranslation,
  currentBook,
  currentChapter,
}: any) {
  const currentChapters = currentBook.bible_chapters;
  const currentSearch = useCurrentSearch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeChapterRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const activeEl = activeChapterRef.current;
    if (!container || !activeEl) return;

    const ensureFullyVisible = () => {
      const containerRect = container.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();
      if (activeRect.left >= containerRect.left && activeRect.right <= containerRect.right) return;

      // Move only the chapter strip; scrollIntoView would also move the verses.
      container.scrollTo({
        left: container.scrollLeft + (activeRect.left + activeRect.width / 2)
          - (containerRect.left + containerRect.width / 2),
      });
    };

    const raf = requestAnimationFrame(ensureFullyVisible);
    const observer = new ResizeObserver(ensureFullyVisible);
    observer.observe(container);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [currentTranslation.slug, currentBook.slug, currentChapter.chapter_number]);

  return (
    <nav className="bible-bottom-bar" aria-label="Chapters">
      <Link
        href={currentChapter.previous_chapter_path + currentSearch}
        preserveState
        only={['chosen_book', 'chosen_chapter', 'verses']}
        className="bible-chapter-link"
        aria-label="Previous chapter"
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </Link>
      <div className="bible-chapter-strip hide-scrollbar" ref={scrollContainerRef}>
        {currentChapters.map((chapter: any) => {
          const isActive = chapter.chapter_number === currentChapter.chapter_number;
          return (
            <BibleLink
              key={chapter.chapter_number}
              currentTranslationSlug={currentTranslation.slug}
              currentBookSlug={currentBook.slug}
              currentChapterNumber={currentChapter.chapter_number}
              chapterNumber={chapter.chapter_number}
              className="bible-chapter-link"
              aria-current={isActive ? 'page' : undefined}
              ref={isActive ? activeChapterRef : undefined}
            >
              {chapter.chapter_number}
            </BibleLink>
          );
        })}
      </div>
      <Link
        href={currentChapter.next_chapter_path + currentSearch}
        preserveState
        only={['chosen_book', 'chosen_chapter', 'verses']}
        className="bible-chapter-link"
        aria-label="Next chapter"
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </Link>
    </nav>
  );
}
